import React, { useContext, useEffect, useReducer } from "react";
import { 
    Alert, Button, Dialog, DialogActions, DialogContent, 
    DialogTitle, Divider, IconButton, List, ListItem, 
    ListItemAvatar, ListItemText, MenuItem, Paper, Snackbar, 
    TextField, Typography, Box, Chip, Avatar, useTheme 
} from "@mui/material";

import { LoadingButton } from "@mui/lab";
import { AddRedSocial, GetRedesSociales, UpdateRedSocial } from "../../../controllers/company/CompanyProfileController";
import UserContext from "../../../navigation/context/userContext";

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram, faLinkedin, faWhatsapp, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { faX } from "@fortawesome/free-solid-svg-icons";
import { Add, Edit, Link } from "@mui/icons-material";

// Mapa de colores oficiales
const brandColors = {
    'Instagram': '#E1306C',
    'WhatsApp': '#25D366',
    'X' : '#000000',
    'Facebook': '#1877F2',
    'Linkedin': '#0077B5',
    'YouTube' : '#FF0000'
};

const iconMapping = {
    'Instagram': faInstagram,
    'WhatsApp': faWhatsapp,
    'X' : faX,
    'Facebook': faFacebook,
    'Linkedin': faLinkedin,
    'YouTube' : faYoutube
}

const initialState = {
    confSocials: [], redesSociales: [], uuid: '',
    idRedSocial: '', errorIdRedSocial: false,
    enlace: '', errorEnlace: false,
    username: '', errorUsername: false,
    estatus: '', errorEstatus: false,
    operacion: 'agregar', isLoading: false,
    open: false, openAlert: false, message: '', success: false
};

function reducer(state, { field, value }) {
    return { ...state, [field]: value };
}

export default function Socials() {
    const theme = useTheme();
    const { userData } = useContext(UserContext);
    const [state, setState] = useReducer(reducer, initialState);

    const {
        confSocials, redesSociales, uuid,
        idRedSocial, errorIdRedSocial, enlace, errorEnlace,
        username, errorUsername, estatus, errorEstatus,
        operacion, isLoading, open, openAlert, message, success
    } = state;

    useEffect(() => {
        handleGetRedesSociales();
    }, []);

    const onChange = (e) => {
        setState({ field: e.target.name, value: e.target.value });
    }

    const handleGetRedesSociales = async () => {
        let response = await GetRedesSociales();
        if (response.success === true) {
            setState({ field: 'confSocials', value: response.data.confSocials });
            setState({ field: 'redesSociales', value: response.data.redesSociales });
        }
    }

    const cancel = () => {
        setState({ field: 'enlace', value: '' });
        setState({ field: 'username', value: '' });
        setState({ field: 'idRedSocial', value: '' });
        setState({ field: 'estatus', value: '1' });
        setState({ field: 'errorEnlace', value: false });
        setState({ field: 'errorUsername', value: false });
        setState({ field: 'errorIdRedSocial', value: false });
        setState({ field: 'errorEstatus', value: false });
    }

    const handleOpenAdd = () => {
        setState({ field: 'operacion', value: 'agregar' });
        cancel();
        setState({ field: 'open', value: true });
    }

    const handleOpenUpdate = (item) => {
        setState({ field: 'operacion', value: 'actualizar' });
        setState({ field: 'uuid', value: item.uuid });
        setState({ field: 'enlace', value: item.enlace });
        setState({ field: 'username', value: item.username });
        setState({ field: 'idRedSocial', value: item.idRedSocial });
        setState({ field: 'estatus', value: item.estatus });
        setState({ field: 'open', value: true });
    }

    const handleClose = () => {
        cancel();
        setState({ field: 'open', value: false })
    }

    const handleAddSubmit = async () => {
        if (!idRedSocial || !username) return;

        setState({ field: 'isLoading', value: true });
        let response;
        if (operacion === 'agregar') {
            response = await AddRedSocial(idRedSocial, username, enlace, estatus);
        } else {
            response = await UpdateRedSocial(uuid, idRedSocial, username, enlace, estatus);
        }

        setState({ field: 'message', value: response.message });
        setState({ field: 'success', value: response.success });
        setState({ field: 'openAlert', value: true });
        setState({ field: 'isLoading', value: false });
        
        handleClose();
        handleGetRedesSociales();
    }

    const getRRSSName = (_id) => {
        let rrss = confSocials.find(item => item.id === _id);
        return rrss ? rrss.nombre : '';
    }

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 3,
                overflow: 'hidden'
            }}
        >
            {/* --- HEADER BLANCO (Corrección Solicitada) --- */}
            <Box sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                bgcolor: '#FFFFFF', // Blanco puro
                borderBottom: '1px solid #eee'
            }}>
                <Typography variant="h6" fontWeight="bold">
                    Redes Sociales
                </Typography>
                <IconButton 
                    size='small' 
                    onClick={() => handleOpenAdd()} 
                    sx={{ 
                        border: '1px solid #eee',
                        '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                >
                    <Add fontSize="small"/>
                </IconButton>
            </Box>
            
            <List sx={{ p: 0 }}>
                {redesSociales.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                         <Typography variant="body2" color="text.secondary">
                            No has vinculado ninguna red social.
                         </Typography>
                    </Box>
                )}

                {redesSociales.map((item, index) => {
                    const rrssName = getRRSSName(item.idRedSocial);
                    const color = brandColors[rrssName] || '#555';
                    
                    return (
                        <React.Fragment key={item.id}>
                            <ListItem
                                secondaryAction={
                                    <IconButton edge='end' size="small" onClick={() => handleOpenUpdate(item)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                }
                                sx={{ py: 1.5 }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ 
                                        bgcolor: `${color}15`, // Fondo transparente con tinte de marca
                                        color: color, 
                                        borderRadius: 2 
                                    }}>
                                        <FontAwesomeIcon icon={iconMapping[rrssName] || Link} />
                                    </Avatar>
                                </ListItemAvatar>
                                
                                <ListItemText
                                    primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight="bold">{rrssName}</Typography>
                                            <Chip 
                                                label={item.estatus == 1 ? "Activo" : "Inactivo"} 
                                                size="small" 
                                                sx={{ 
                                                    height: 20, 
                                                    fontSize: '0.65rem', 
                                                    bgcolor: item.estatus == 1 ? 'success.lighter' : 'grey.100',
                                                    color: item.estatus == 1 ? 'success.main' : 'grey.500',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {item.username}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            {/* Divider sutil */}
                            {index < redesSociales.length - 1 && <Divider component="li" variant="inset" />}
                        </React.Fragment>
                    )
                })}
            </List>

            {/* --- DIALOG MODAL --- */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {operacion === 'agregar' ? 'Vincular Red Social' : 'Editar Conexión'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth margin="dense" select
                            label="Plataforma"
                            name='idRedSocial'
                            value={idRedSocial}
                            onChange={onChange}
                            error={errorIdRedSocial}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        >
                            {confSocials.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
                        </TextField>

                        <TextField
                            fullWidth margin="dense"
                            label="Enlace (URL)"
                            name='enlace'
                            value={enlace}
                            onChange={onChange}
                            error={errorEnlace}
                            placeholder="https://..."
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />

                        <TextField
                            fullWidth margin="dense"
                            label="Usuario (@)"
                            name='username'
                            value={username}
                            onChange={onChange}
                            error={errorUsername}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />

                        <TextField
                            fullWidth margin="dense" select
                            label="Estado"
                            name='estatus'
                            value={estatus}
                            onChange={onChange}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        >
                            <MenuItem value='1'>Activo</MenuItem>
                            <MenuItem value='0'>Inactivo</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={handleClose} color="inherit" sx={{ borderRadius: 2 }}>Cancelar</Button>
                    <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        onClick={handleAddSubmit}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Guardar
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={openAlert}
                autoHideDuration={4000}
                onClose={() => setState({ field: 'openAlert', value: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={success ? 'success' : 'error'} variant="filled" sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </Paper>
    )
}